import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useClients } from "../contexts/ClientsContext";
import Studio from "../components/Studio";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  LogOut,
  Mail,
  Trash2,
  CheckCircle2,
  Circle,
  Lock,
  Inbox,
  RefreshCw,
  Palette,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cb_admin_token";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const LoginCard = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back, Christina.");
      onLogin(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#0A0A0A] border border-stone-800 rounded-sm p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-6">
          <Lock size={20} className="text-[#F62493]" />
          <p className="font-mono-label text-stone-300">Admin Access</p>
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-stone-100 leading-tight mb-2">
          Welcome back.
        </h1>
        <p className="text-stone-200 mb-8">
          Enter the admin password to view portfolio inquiries.
        </p>

        <Label htmlFor="pw" className="font-mono-label text-stone-300">
          Password
        </Label>
        <Input
          id="pw"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoFocus
          className="mt-2 mb-6 bg-transparent border-0 border-b border-stone-400 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#F62493] text-lg"
          placeholder="••••••••••"
        />

        <button
          type="submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-6 py-3 hover:bg-[#F62493] disabled:opacity-60 transition-colors"
        >
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const InquiryRow = ({ item, onMarkRead, onMarkUnread, onDelete, onOpen, isOpen }) => {
  return (
    <div
      className={`border-t border-stone-800 ${
        item.read ? "" : "bg-[#1F1A0F]"
      } transition-colors`}
    >
      <button
        onClick={() => onOpen(isOpen ? null : item.id)}
        className="w-full grid grid-cols-12 gap-4 items-baseline text-left py-5 px-4 hover:bg-[#1A1A1A]/60 transition-colors"
      >
        <div className="col-span-1 flex items-center justify-center">
          {item.read ? (
            <Circle size={14} className="text-stone-300" />
          ) : (
            <span className="w-2 h-2 rounded-full bg-[#F62493] inline-block" />
          )}
        </div>
        <div className="col-span-3">
          <div className={`font-display text-lg ${item.read ? "text-stone-200" : "text-stone-100"}`}>
            {item.name}
          </div>
          <div className="font-mono-label text-stone-300 mt-1">{item.email}</div>
        </div>
        <div className="col-span-3 text-stone-200 text-sm">
          {item.company || <span className="text-stone-300">—</span>}
          <div className="font-mono-label text-stone-300 mt-1">
            {item.project || ""}
          </div>
        </div>
        <div className="col-span-3 truncate text-stone-200 text-sm">{item.message}</div>
        <div className="col-span-2 text-right font-mono-label text-stone-300 text-xs">
          {fmtDate(item.created_at)}
        </div>
      </button>

      {isOpen && (
        <div className="bg-[#141414] px-6 py-6 border-t border-stone-800">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <p className="font-mono-label text-stone-300 mb-2">Message</p>
              <p className="whitespace-pre-wrap text-stone-200 leading-relaxed">
                {item.message}
              </p>
            </div>
            <div className="space-y-3">
              <a
                href={`mailto:${item.email}?subject=Re: your portfolio inquiry`}
                className="inline-flex items-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-5 py-2.5 hover:bg-[#F62493] transition-colors"
              >
                <Mail size={14} /> Reply
              </a>
              {item.read ? (
                <button
                  onClick={() => onMarkUnread(item.id)}
                  className="w-full inline-flex items-center gap-2 rounded-full border border-stone-400 text-stone-200 px-5 py-2.5 hover:bg-[#242424] transition-colors"
                >
                  <Circle size={14} /> Mark as unread
                </button>
              ) : (
                <button
                  onClick={() => onMarkRead(item.id)}
                  className="w-full inline-flex items-center gap-2 rounded-full border border-stone-400 text-stone-200 px-5 py-2.5 hover:bg-[#242424] transition-colors"
                >
                  <CheckCircle2 size={14} /> Mark as read
                </button>
              )}
              <button
                onClick={() => onDelete(item)}
                className="w-full inline-flex items-center gap-2 rounded-full border border-red-300 text-red-700 px-5 py-2.5 hover:bg-red-50 transition-colors"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | unread | read
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tab, setTab] = useState("inbox"); // inbox | studio
  const { clients, reload: reloadClients } = useClients();

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogin = (t) => {
    setToken(t);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems([]);
    toast.success("Logged out.");
  };

  const markRead = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/admin/inquiries/${id}/read`, {}, { headers });
      setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
    } catch {
      toast.error("Could not update.");
    }
  };

  const markUnread = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/admin/inquiries/${id}/unread`, {}, { headers });
      setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
    } catch {
      toast.error("Could not update.");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers });
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success("Inquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!token) return <LoginCard onLogin={handleLogin} />;

  const filtered = items.filter((it) => {
    if (filter === "unread") return !it.read;
    if (filter === "read") return it.read;
    return true;
  });
  const unreadCount = items.filter((it) => !it.read).length;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-24">
      {/* Header with title + signout */}
      <div className="flex items-start justify-between gap-6 mb-8 md:mb-10">
        <div>
          <p className="font-mono-label text-[#F62493] mb-3">Admin</p>
          <h1 className="font-display text-4xl md:text-6xl text-stone-100 leading-tight">
            Portfolio Admin
          </h1>
        </div>
        <button
          onClick={logout}
          className="inline-flex items-center gap-2 rounded-full border border-stone-400 text-stone-200 px-4 py-2 hover:bg-[#1A1A1A] transition-colors"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-10 border-b border-stone-800 pb-0">
        {[
          { k: "inbox", l: "Inbox", icon: Inbox },
          { k: "studio", l: "Content Studio", icon: Palette },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-2 font-mono-label text-xs px-5 py-3 -mb-px border-b-2 transition-colors ${
              tab === t.k
                ? "border-[#F62493] text-[#F62493]"
                : "border-transparent text-stone-300 hover:text-stone-100"
            }`}
          >
            <t.icon size={14} /> {t.l}
            {t.k === "inbox" && unreadCount > 0 && (
              <span className="ml-1 px-2 py-0.5 rounded-full bg-[#F62493] text-[#F7F3ED] text-[9px]">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "studio" ? (
        <Studio token={token} clients={clients} reloadClients={reloadClients} />
      ) : (
        <>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <p className="font-mono-label text-stone-300 mb-2">Contact form messages</p>
              <p className="text-stone-200">
                {items.length} total · <span className="text-[#F62493]">{unreadCount} unread</span>
              </p>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-stone-400 text-stone-200 px-4 py-2 hover:bg-[#1A1A1A] disabled:opacity-60 transition-colors w-fit"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>

          <div className="flex gap-2 mb-6">
            {[
              { k: "all", l: "All" },
              { k: "unread", l: `Unread (${unreadCount})` },
              { k: "read", l: "Read" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`font-mono-label px-4 py-2 rounded-full border transition-colors ${
                  filter === f.k
                    ? "bg-[#171717] text-[#F7F3ED] border-stone-900"
                    : "border-stone-400 text-stone-200 hover:border-stone-900"
                }`}
              >
                {f.l}
              </button>
            ))}
      </div>

      <div className="border border-stone-800 rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-[#1A1A1A] font-mono-label text-stone-300 text-[10px]">
          <div className="col-span-1"></div>
          <div className="col-span-3">From</div>
          <div className="col-span-3">Company / Project</div>
          <div className="col-span-3">Message</div>
          <div className="col-span-2 text-right">Received</div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 text-center">
            <Inbox size={32} className="mx-auto text-stone-300 mb-4" />
            <p className="font-display text-2xl text-stone-200">
              {items.length === 0 ? "No inquiries yet." : "Nothing matches that filter."}
            </p>
            <p className="text-stone-300 mt-2">
              {items.length === 0
                ? "When someone fills out your contact form, it'll appear here."
                : "Try a different filter."}
            </p>
          </div>
        ) : (
          filtered.map((it) => (
            <InquiryRow
              key={it.id}
              item={it}
              isOpen={openId === it.id}
              onOpen={(id) => {
                setOpenId(id);
                if (id && !it.read) markRead(id);
              }}
              onMarkRead={markRead}
              onMarkUnread={markUnread}
              onDelete={(item) => setConfirmDelete(item)}
            />
          ))
        )}
      </div>
        </>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete
                ? `From ${confirmDelete.name} (${confirmDelete.email}). This can't be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
